import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';
import { TimeBasedMetricsSchema } from '@/warranty-v3/entities/metric';

import { metrics } from './__mocked_data__/time-based-metrics';
import { metricsOutputMapper } from './mappers/metrics-output-mapper';

export namespace GetMetricsQuery {
  export const key = (input: Input) => [
    'warranty-v3',
    'metrics',
    input.systemId,
    input.alertType,
  ];

  export type Input = {
    systemId: string;
    alertType?:
      | 'warranty_rte'
      | 'warranty_soh'
      | 'warranty_tag'
      | 'warranty_tag_pcs';
  };
  export const OutputSchema = TimeBasedMetricsSchema;
  export type Output = z.infer<typeof OutputSchema>;

  function useQueryFn() {
    const me = useMe();
    const metricsRetrieve = useInjectFeApiContext(
      FeApi.warrantyV2MetricsRetrieve,
    );

    return async function queryFn(input: Input): Promise<Output> {
      if (me.config.id === '49') {
        return metrics(input);
      }

      const result = await metricsRetrieve({
        systemId: input.systemId,
        findingType: input.alertType,
      });

      if (result.status !== 200) {
        throw new Error('Failed to fetch metrics');
      }

      const output = await OutputSchema.safeParseAsync(
        metricsOutputMapper(result.data),
      );

      if (!output.success) {
        throw new Error('Failed to parse fe_api metrics data', {
          cause: output.error,
        });
      }

      return output.data;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
      keepPreviousData: true,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
